import { TypedDocumentNode, gql } from '@apollo/client';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL, LabelM } from '@sorare/core/src/atoms/typography';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_PLAY } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';

import { CraftingExperience } from 'components/essence/CraftingExperience';
import { EssenceOddsDialog } from 'components/essence/EssenceOddsDialog';

import {
  InventoryShardsPageQuery,
  InventoryShardsPageQueryVariables,
  MarkShardsThresholdAsSeen,
  MarkShardsThresholdAsSeenVariables,
} from './__generated__/index.graphql';

const INVENTORY_SHARDS_TYPE_PAGE_QUERY = gql`
  query InventoryShardsPageQuery($sport: Sport!) {
    currentUser {
      slug
      cardShardsChests(sport: $sport) {
        id
        rarity
        newThresholdUnlocked
        ...CraftingExperience_cardShardsChest
      }
    }
  }
  ${CraftingExperience.fragments.cardShardsChest}
` as TypedDocumentNode<
  InventoryShardsPageQuery,
  InventoryShardsPageQueryVariables
>;

const MARK_SHARDS_TRESHOLD_AS_SEEN = gql`
  mutation MarkShardsThresholdAsSeen(
    $input: markCardShardsChestUnlockedThresholdAsSeenInput!
  ) {
    markCardShardsChestUnlockedThresholdAsSeen(input: $input) {
      cardShardsChest {
        id
        newThresholdUnlocked
      }
    }
  }
` as TypedDocumentNode<
  MarkShardsThresholdAsSeen,
  MarkShardsThresholdAsSeenVariables
>;

const PageHeaderPortal = styled.div`
  padding: var(--unit);
`;
const Root = styled(Vertical)`
  height: 100%;
`;
const Content = styled.div`
  height: 100%;
  min-height: 0;
  flex: 1;
  position: relative;
  isolation: isolate;
`;
const StyledIconButton = styled(IconButton)`
  position: absolute;
  right: var(--double-unit);
  top: var(--double-unit);
`;

type Props = {
  sport: Sport;
  type: Rarity;
  withPageHeader?: boolean;
};

export const InventoryShardsTypePage = ({
  sport,
  type,
  withPageHeader = true,
}: Props) => {
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const { generateSportContextPath } = useSportContext();
  const [oddsDialogOpen, setOddsDialogOpen] = useState(false);
  const [markAsSeen] = useMutation(MARK_SHARDS_TRESHOLD_AS_SEEN);

  const { data } = useQuery(INVENTORY_SHARDS_TYPE_PAGE_QUERY, {
    variables: {
      sport,
    },
  });

  const { cardShardsChests } = data?.currentUser || {};
  const shardsChest = cardShardsChests?.find(chest => chest.rarity === type);

  useEffect(() => {
    if (shardsChest?.newThresholdUnlocked) {
      markAsSeen({
        variables: {
          input: {
            cardShardsChestId: shardsChest.id,
          },
        },
      });
    }
  }, [markAsSeen, shardsChest]);

  if (!shardsChest) {
    return null;
  }

  return (
    <>
      {withPageHeader && (
        <PageHeader
          backTo={generateSportContextPath(ANY_SPORT_PLAY)}
          text={isDesktopAndAbove ? glossary.inventory : undefined}
          customTitle={
            !isDesktopAndAbove ? (
              <SBHorizontal>
                <Vertical gap={0}>
                  <HeadlineL as="p">
                    <FormattedMessage {...glossary.inventory} />
                  </HeadlineL>
                  <LabelM as="p">
                    <FormattedMessage {...glossary.essence} />
                  </LabelM>
                </Vertical>
                <StyledIconButton
                  color="transparent"
                  icon={faCircleInfo}
                  onClick={() => setOddsDialogOpen(true)}
                />
              </SBHorizontal>
            ) : undefined
          }
        />
      )}
      <ConditionalWrapper
        Wrapper={FullWidthContainer.NoPadding}
        wrap={isDesktopAndAbove}
      >
        <Root>
          {withPageHeader && !isDesktopAndAbove && (
            <PageHeaderPortal id="page-header-title" />
          )}
          <Content>
            <CraftingExperience cardShardsChest={shardsChest} />
            {isDesktopAndAbove && (
              <StyledIconButton
                color="transparent"
                icon={faCircleInfo}
                onClick={() => setOddsDialogOpen(true)}
              />
            )}
          </Content>
        </Root>
        {oddsDialogOpen && (
          <EssenceOddsDialog
            open
            onClose={() => setOddsDialogOpen(false)}
            rarity={shardsChest.rarity}
          />
        )}
      </ConditionalWrapper>
    </>
  );
};
